import React, {FC, useState, useEffect} from "react"

import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {Body, Label, H3} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import Count from "@irati/ui/src/Count"
import {ReactTable} from "@irati/ui/src/Table"
import Spacing from "@irati/ui/src/Spacing"
import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import {TeamReport, ReportingTeamState} from "@irati/types"

import EditTeamButton from "./EditTeamButton"
import columns from "./columns"
import {ExpandedTeamInfo} from "./styled"

interface Props extends InjectIntl {
  title: string,
  teams: TeamReport[]
}

interface expandedState {
  [key: number]: boolean
}

const PAGE_LIMIT = 5
const DEFAULT_EXPANDED = {0: false}

const TeamBreakdown: FC<Props> = ({title, teams, formatMessage}) => {
  const [page, setPage] = useState<number>(0)
  const [expanded, setExpanded] = useState<expandedState>(DEFAULT_EXPANDED)
  const [expandedIndex, setExpandedIndex] = useState<number | undefined>(undefined)
  const handlePageChange = (nextPage: number) => {
    setPage(nextPage)
    setExpanded(DEFAULT_EXPANDED)
    setExpandedIndex(undefined)
  }
  const pages = Math.ceil(teams.length / PAGE_LIMIT)

  useEffect(() => {
    handlePageChange(0)
  }, [teams])

  return (
    <Card flush id="adminSection_teamEngagementBreakdown">
      <Flex alignItems="baseline">
        <H3 margin={[4, 0, 2, 4]}>
          <FormattedMessage id={title} />
        </H3>
        <Count
          backgroundColor={theme.colors.tertiary.b_400}
          textColor={theme.colors.greys.white}>
          {teams.length} {formatMessage({id: "admin.components.teamEngagement.teamBreakdown.title"})}
        </Count>
      </Flex>
      {teams?.length
        ? <ReactTable
          manual={false}
          sortable={false}
          getPaginationProps={(allProps: any) => ({
            ...allProps,
            shouldScrollOnUpdate: false
          })}
          page={page}
          pages={pages}
          onPageChange={handlePageChange}
          showPagination={pages > 1}
          data={teams.sort((a, b) => a.engaged - b.engaged)}
          defaultPageSize={PAGE_LIMIT}
          onExpandedChange={(_expanded, index) => {
            const expandedKey = index[0]
            const showExpanded = expanded && !expanded[index[0]]
            const newExpandedIndex = showExpanded ? expandedKey + (page * PAGE_LIMIT) : undefined
            setExpanded({[expandedKey]: showExpanded})
            setExpandedIndex(newExpandedIndex)
          }}
          expanded={expanded}
          SubComponent={(row) => {
            return (
              <ExpandedTeamInfo>
                <Flex>
                  <Spacing>
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label small>{formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.activated"})}</Label>
                      <Body small>{row.original?.activeUsers}</Body>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label small>{formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.people"})}</Label>
                      <Body small>{row.original?.enabledUsers}</Body>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label small>{formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.manager"})}</Label>
                      <Body small>{row.original?.managers}</Body>
                    </Flex>
                    <EditTeamButton
                      uuid={row.original?.uuid}
                      teamName={row.original?.teamName}
                      isRegion={row.original?.teamState === ReportingTeamState.BillingUnit} />
                  </Spacing>
                  <Spacing marginLeft="auto">
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label
                        small
                        color={theme.colors.primary1}>
                        {formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.perks"})}
                      </Label>
                      <Body small>{Math.round(row.original?.perksUsage)}%</Body>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label
                        small
                        color={theme.colors.tertiary.p_400}>
                        {formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.celebrations"})}
                      </Label>
                      <Body small>{Math.round(row.original?.recognitionUsage)}%</Body>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label
                        small
                        color={theme.colors.tertiary.i_400}>
                        {formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.wellness"})}
                      </Label>
                      <Body small>{Math.round(row.original?.wellnessUsage)}%</Body>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center" width="150px" marginBottom={2}>
                      <Label
                        small
                        color={theme.colors.secondary1}>
                        {formatMessage({id: "admin.components.teamEngagement.teamBreakdown.expanded.cultureHub"})}
                      </Label>
                      <Body small>{Math.round(row.original?.cultureHubUsage)}%</Body>
                    </Flex>
                  </Spacing>
                </Flex>
              </ExpandedTeamInfo>
            )
          }}
          columns={columns(formatMessage, expandedIndex)} />
        : <Body padding={[0, 0, 4, 4]}><FormattedMessage id="admin.components.teamEngagement.teamBreakdown.empty" /></Body>}
    </Card>
  )

}
export default injectIntl(TeamBreakdown)
